import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiClient, ApiError, type CommunitySkillSummary } from '../lib/api';
import { getSession } from '../lib/auth';

function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const d = Math.round(ms / (1000 * 60 * 60 * 24));
  if (d < 1) return 'today';
  if (d < 30) return `${d}d ago`;
  const mo = Math.round(d / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.round(mo / 12)}y ago`;
}

export function Community() {
  const navigate = useNavigate();
  const session = getSession();
  const [results, setResults] = useState<CommunitySkillSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<'stars' | 'updated'>('stars');
  const [importing, setImporting] = useState<string | null>(null);

  const load = async () => {
    try {
      const { results: r } = await apiClient.community.browse({ q: search, sort, limit: 50 });
      setResults(r);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load');
    }
  };

  useEffect(() => {
    void load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    void load();
  };

  const handleAdd = async (skill: CommunitySkillSummary) => {
    if (!session) {
      navigate('/login', { state: { from: { pathname: '/community' } } });
      return;
    }
    setImporting(skill.id);
    try {
      const imported = await apiClient.publish.fromGithub({ url: skill.source_url });
      navigate(`/skill/${encodeURIComponent(imported.id)}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Import failed');
      setImporting(null);
    }
  };

  const grouped = useMemo(() => {
    if (!results) return {};
    const by: Record<string, CommunitySkillSummary[]> = {};
    for (const r of results) {
      const key = r.source_repo ?? 'standalone';
      (by[key] = by[key] ?? []).push(r);
    }
    return by;
  }, [results]);

  return (
    <div className="mx-auto max-w-5xl overflow-y-auto p-6">
      <div className="flex items-center justify-between">
        {session ? (
          <Link to="/library" className="text-[12px] text-text-muted hover:text-[var(--color-fg)]">
            ← Library
          </Link>
        ) : (
          <span className="text-[12px] text-text-subtle">Browse mode · no account needed</span>
        )}
      </div>
      <h1 className="mt-3 text-xl font-semibold">Community</h1>
      <p className="mt-1 text-[13px] text-text-subtle">
        {session ? (
          <>
            Skills indexed from public GitHub repos. Click "Add to library" to import a copy you
            can edit and customise. Variables are auto-matched against your vault on import.
          </>
        ) : (
          <>
            Browse AI skills from across the open-source community. Sign in to add any of them
            to your library — variables auto-match against your vault.
          </>
        )}
      </p>

      <div className="mt-5 grid grid-cols-[1fr_auto_auto] gap-2">
        <form onSubmit={handleSearch}>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search description or name…"
            className="w-full rounded border bg-surface px-3 py-1.5 text-[13px] outline-none focus:border-[var(--color-accent)]"
          />
        </form>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value as 'stars' | 'updated')}
          className="rounded border bg-surface px-2 py-1.5 text-[12px]"
        >
          <option value="stars">Sort: stars</option>
          <option value="updated">Sort: recently updated</option>
        </select>
        <button type="button" onClick={() => void load()} className="btn">
          Search
        </button>
      </div>

      {error && (
        <p className="mt-3 rounded border bg-danger-soft p-2 text-[11px] text-[var(--color-danger)]">
          {error}
        </p>
      )}

      {results === null && !error && (
        <p className="mt-6 text-text-subtle">Loading…</p>
      )}

      {results?.length === 0 && (
        <div className="mt-6 rounded border bg-surface p-6 text-center">
          <p className="text-[13px]">No results.</p>
          <p className="mt-1 text-[11px] text-text-subtle">
            The indexer may still be warming up. An admin can trigger a re-index at{' '}
            <code className="font-mono">POST /admin/community/reindex</code>.
          </p>
        </div>
      )}

      {results && results.length > 0 && (
        <div className="mt-5 space-y-6">
          {Object.entries(grouped).map(([repo, skills]) => (
            <section key={repo}>
              <header className="flex items-baseline gap-2">
                <h2 className="font-mono text-[12px]">{repo}</h2>
                <span className="text-[10px] text-text-subtle">
                  {skills[0].stars.toLocaleString()} ⭐ ·{' '}
                  {skills.length} skill{skills.length === 1 ? '' : 's'}
                </span>
              </header>
              <ul className="mt-2 divide-y rounded border bg-canvas">
                {skills.map((s) => (
                  <li
                    key={s.id}
                    className="grid grid-cols-[1fr_auto] items-center gap-3 px-4 py-3 hover:bg-surface-2"
                  >
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-[13px]">{s.name}</span>
                        {s.tags.slice(0, 3).map((t) => (
                          <span key={t} className="chip">
                            {t}
                          </span>
                        ))}
                      </div>
                      {s.description && (
                        <p className="mt-0.5 line-clamp-2 text-[12px] text-text-muted">
                          {s.description}
                        </p>
                      )}
                      <p className="mt-1 text-[10px] text-text-subtle">
                        Updated {relativeTime(s.last_updated)} ·{' '}
                        <a
                          href={s.source_url}
                          target="_blank"
                          rel="noreferrer"
                          className="hover:underline"
                        >
                          source ↗
                        </a>
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => void handleAdd(s)}
                      disabled={importing === s.id}
                      className="btn"
                    >
                      {importing === s.id
                        ? 'Importing…'
                        : session
                          ? 'Add to library'
                          : 'Sign in to add'}
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
